import { useState } from "react";
import { useForm } from "react-hook-form";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import { tokens } from "@core/theme/tokens";
import { CursorTable, type CursorTableColumn } from "@core/table";
import {
  AlertText,
  DialogActions,
  DialogHeader,
  EditorModal,
  type EditorModalTarget,
  Field,
  ImpactConfirmDialog,
  inputStyle,
  PortalButton,
  RowIconAction,
  useToast,
} from "@portal/common";
import { catalogLabel } from "../points/api";
import { useAdjustmentReasons } from "../points/pointQueries";
import type { Redemption, RedemptionFilters } from "./api";
import {
  reverseRedemptionSchema,
  type ReverseRedemptionValues,
} from "./redemptionSchemas";
import { useRedemptions, useReverseRedemption } from "./redemptionQueries";

// SPEC-220 §7: textos literales, no se reescriben. Constantes propias en vez
// de repetirlas en cada rama del flujo de reversión.
const REASON_REQUIRED_MESSAGE = "Indica el motivo y el detalle de la reversión";
const INSUFFICIENT_PERMISSION_MESSAGE =
  "No tienes permiso para revertir consumos";
// QA-MTZ-002 #8: la reversión propaga el código real del rechazo (antes todo
// caía en un 404 sin mensaje y el toast mostraba el genérico con
// correlación). Textos de `api/src/infrastructure/http/problem.ts`.
const REVERSE_ERROR_MESSAGES: Partial<Record<string, string>> = {
  RESOURCE_NOT_FOUND: "Este consumo ya no está disponible; actualiza la lista.",
  MOVEMENT_VOIDED: "Este movimiento ya fue anulado",
  PRIZE_DELIVERED:
    "El premio de este avance ya fue entregado y no puede revertirse",
};
// §4.2 punto 4 y criterio 10: las dos confirmaciones dicen explícitamente que
// el código QR no se reactiva. `web/AGENTS.md`/ADR-004: el título y el
// `detail` del `Problem` del servidor no se muestran crudos al usuario
// (`core/http/ApiError.ts`) — el mensaje se arma acá con el único dato
// numérico que el dominio no puede inventar del lado del cliente
// (`impact.pointsToReturn`).
const confirmPointsMessage = (pointsToReturn: number | undefined) =>
  `Se devolverán ${pointsToReturn ?? "—"} puntos al miembro. El código no volverá a ser utilizable. ¿Confirmas?`;
const CONFIRM_BENEFIT_MESSAGE =
  "El beneficio quedará disponible otra vez en este período. El código no volverá a ser utilizable. ¿Confirmas?";

const KIND_LABEL: Record<Redemption["kind"], string> = {
  benefit: "Beneficio",
  reward: "Recompensa",
};

const dateTimeFormatter = new Intl.DateTimeFormat("es-EC", {
  timeZone: "America/Guayaquil",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function formatDateTime(value: string): string {
  const parts = dateTimeFormatter.formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("day")} ${part("month")} ${part("year")} · ${part("hour")}:${part("minute")}`;
}

// SPEC-220 §7: "Este consumo ya fue revertido el DD/MM" — solo día y mes, sin
// año ni hora.
function formatDayMonth(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
}

type ReverseStep = "form" | "confirm-points" | "confirm-benefit";

function ReverseForm({
  onSubmit,
  onCancel,
  submitting,
  notice,
  modal,
}: {
  onSubmit: (value: ReverseRedemptionValues) => void;
  onCancel: () => void;
  submitting?: boolean;
  notice: string | null;
  // Revisión en vivo 2026-09-22, puntos #3/#4A: el formulario ES el diálogo.
  modal: EditorModalTarget;
}) {
  const reasons = useAdjustmentReasons();
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  const { register, handleSubmit } = useForm<ReverseRedemptionValues>({
    defaultValues: { adjustmentReasonId: "", detail: "" },
  });

  return (
    <EditorModal
      modal={modal}
      // Punto #21 (revisión en vivo 2026-09-22): encabezado común.
      header={<DialogHeader eyebrow="Consumos" title="Revertir consumo" />}
      footer={
        <DialogActions onCancel={onCancel}>
          <PortalButton type="submit" size="small" disabled={submitting}>
            {submitting ? "Continuando…" : "Continuar"}
          </PortalButton>
        </DialogActions>
      }
      onSubmit={handleSubmit((value) => {
        const parsed = reverseRedemptionSchema.safeParse(value);
        if (!parsed.success) {
          setLocalNotice(REASON_REQUIRED_MESSAGE);
          return;
        }
        setLocalNotice(null);
        onSubmit(parsed.data);
      })}
    >
      <Field label="Motivo del ajuste" required>
        <select
          aria-label="Motivo del ajuste"
          style={inputStyle}
          {...register("adjustmentReasonId")}
        >
          <option value="">Selecciona un motivo…</option>
          {(reasons.data ?? []).map((entry) => (
            <option key={entry.id} value={entry.id}>
              {catalogLabel(entry)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Detalle" required>
        <textarea
          aria-label="Detalle"
          style={{ ...inputStyle, minHeight: 66, resize: "vertical" }}
          {...register("detail")}
        />
      </Field>
      {(localNotice ?? notice) ? (
        <div
          role="alert"
          style={{
            marginBottom: 6,
            padding: "11px 14px",
            borderRadius: 11,
            border: `1px solid ${tokens.danger.line}`,
            background: tokens.danger.dim,
            font: `400 12px ${tokens.font.ui}`,
            color: tokens.danger.base,
          }}
        >
          {localNotice ?? notice}
        </div>
      ) : null}
    </EditorModal>
  );
}

export function RedemptionsPanel({
  memberId,
  memberName,
}: {
  memberId?: string;
  memberName?: string;
}) {
  const fixedMember = Boolean(memberId);
  const [memberFilter, setMemberFilter] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [kind, setKind] = useState<"all" | Redemption["kind"]>("all");
  const [reversed, setReversed] = useState<"all" | "false" | "true">("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filters: RedemptionFilters = {
    ...(fixedMember
      ? { memberId }
      : memberFilter
        ? { memberId: memberFilter }
        : {}),
    ...(merchantId ? { merchantId } : {}),
    ...(kind !== "all" ? { kind } : {}),
    ...(reversed !== "all" ? { reversed: reversed === "true" } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const redemptions = useRedemptions(filters);
  const rows = redemptions.data?.pages.flatMap((page) => page.items) ?? [];
  const lastPage = redemptions.data?.pages.at(-1);

  const toast = useToast();
  const reverseMutation = useReverseRedemption();
  const [reversing, setReversing] = useState<Redemption | null>(null);
  const [reverseStep, setReverseStep] = useState<ReverseStep>("form");
  const [reverseNotice, setReverseNotice] = useState<string | null>(null);
  const [pointsToReturn, setPointsToReturn] = useState<number | undefined>(
    undefined,
  );
  const [reverseValues, setReverseValues] =
    useState<ReverseRedemptionValues | null>(null);
  const [reverseKey, setReverseKey] = useState<string | null>(null);

  const startReverse = (redemption: Redemption) => {
    setReverseNotice(null);
    setReverseStep("form");
    setReverseValues(null);
    setPointsToReturn(undefined);
    setReverseKey(crypto.randomUUID());
    setReversing(redemption);
  };

  const closeReverse = () => {
    setReversing(null);
    setReverseNotice(null);
    setReverseValues(null);
    setReverseKey(null);
  };

  const submitReverseForm = (values: ReverseRedemptionValues) => {
    if (!reversing || !reverseKey) return;
    setReverseValues(values);
    reverseMutation.mutate(
      {
        id: reversing.id,
        input: { ...values, confirmed: false },
        idempotencyKey: reverseKey,
      },
      {
        onError: (error) => onReverseError(error),
      },
    );
  };

  const confirmReverse = () => {
    if (!reversing || !reverseKey || !reverseValues) return;
    reverseMutation.mutate(
      {
        id: reversing.id,
        input: { ...reverseValues, confirmed: true },
        idempotencyKey: reverseKey,
      },
      {
        onSuccess: () => {
          toast.show("Consumo revertido.");
          closeReverse();
        },
        onError: (error) => onReverseError(error),
      },
    );
  };

  // PLAN-009 §3: `REASON_REQUIRED` es un error de campo (bajo Motivo/
  // Detalle, se queda en el formulario); `CONFIRM_POINTS_REVERSAL`/
  // `CONFIRM_BENEFIT_REVERSAL` cambian de paso (aviso de reconfirmación).
  // Cualquier otro rechazo (permiso insuficiente, ya revertido, un código
  // sin mapear) es un error de servicio → toast.
  const onReverseError = (error: unknown) => {
    if (!(error instanceof ApiError)) {
      reportError(toast, error);
      return;
    }
    if (error.code === "REASON_REQUIRED") {
      setReverseStep("form");
      setReverseNotice(REASON_REQUIRED_MESSAGE);
      markErrorHandled(error);
      return;
    }
    if (error.code === "INSUFFICIENT_PERMISSION") {
      setReverseStep("form");
      toast.show(INSUFFICIENT_PERMISSION_MESSAGE, "error");
      markErrorHandled(error);
      return;
    }
    if (error.code === "REDEMPTION_REVERSED") {
      const impact = error.problem?.impact as
        { reversedAt?: string } | undefined;
      setReverseStep("form");
      toast.show(
        impact?.reversedAt
          ? `Este consumo ya fue revertido el ${formatDayMonth(impact.reversedAt)}`
          : "Este consumo ya fue revertido.",
        "error",
      );
      markErrorHandled(error);
      void redemptions.refetch();
      return;
    }
    if (error.code === "CONFIRM_POINTS_REVERSAL") {
      const impact = error.problem?.impact as
        { pointsToReturn?: number } | undefined;
      setPointsToReturn(impact?.pointsToReturn);
      setReverseStep("confirm-points");
      markErrorHandled(error);
      return;
    }
    if (error.code === "CONFIRM_BENEFIT_REVERSAL") {
      setReverseStep("confirm-benefit");
      markErrorHandled(error);
      return;
    }
    if (error.code === "RESOURCE_NOT_FOUND") void redemptions.refetch();
    reportError(toast, error, REVERSE_ERROR_MESSAGES);
  };

  const columns: CursorTableColumn<Redemption>[] = [
    {
      key: "occurredAt",
      header: "Fecha y hora",
      width: "160px",
      render: (r) => (
        <span style={{ color: tokens.text.tertiary }}>
          {formatDateTime(r.occurredAt)}
        </span>
      ),
    },
    {
      key: "member",
      header: "Miembro",
      width: "1.1fr",
      render: (r) => (
        <span style={{ font: `500 12.5px ${tokens.font.ui}` }}>
          {r.member.legalName ?? r.member.membershipNumber ?? "—"}
        </span>
      ),
    },
    {
      key: "item",
      header: "Ítem consumido",
      width: "1.2fr",
      render: (r) => <span>{r.item?.name ?? "—"}</span>,
    },
    {
      key: "kind",
      header: "Tipo",
      width: "110px",
      render: (r) => <span>{KIND_LABEL[r.kind]}</span>,
    },
    {
      key: "place",
      header: "Comercio",
      width: "1fr",
      render: (r) => <span>{r.place.name}</span>,
    },
    {
      key: "validator",
      header: "Usuario validador",
      width: "140px",
      render: (r) => (
        <span style={{ color: tokens.text.tertiary }}>
          {r.validatorUsername ?? "—"}
        </span>
      ),
    },
    {
      key: "points",
      header: "Puntos descontados",
      width: "130px",
      render: (r) => (
        <span
          style={{
            fontVariantNumeric: "tabular-nums",
            color: tokens.gold.light,
          }}
        >
          {r.pointsDeducted ?? 0}
        </span>
      ),
    },
    {
      key: "status",
      header: "Estado",
      width: "1.3fr",
      render: (r) =>
        r.reversedAt ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="stat stat-neutral">Revertido</span>
            <span
              style={{
                font: `400 11px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
              }}
            >
              Este consumo ya fue revertido el {formatDayMonth(r.reversedAt)}
            </span>
          </div>
        ) : (
          <span className="stat stat-silver">Vigente</span>
        ),
    },
  ];

  return (
    <div>
      <section
        aria-label={
          fixedMember && memberName
            ? `Filtros de consumos de ${memberName}`
            : "Filtros de consumos"
        }
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 12,
          padding: "12px 14px",
          marginBottom: 16,
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: tokens.radius.lg,
          background: tokens.surface.bg2,
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="label">Rango de fechas</span>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              aria-label="Desde"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...inputStyle, width: "auto" }}
            />
            <input
              aria-label="Hasta"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...inputStyle, width: "auto" }}
            />
          </div>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="label">Comercio</span>
          <input
            aria-label="Comercio"
            placeholder="ID de comercio"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            style={{ ...inputStyle, width: "auto", minWidth: 160 }}
          />
        </label>
        {fixedMember ? null : (
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="label">Miembro</span>
            <input
              aria-label="Miembro"
              placeholder="ID de miembro"
              value={memberFilter}
              onChange={(e) => setMemberFilter(e.target.value)}
              style={{ ...inputStyle, width: "auto", minWidth: 160 }}
            />
          </label>
        )}
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="label">Tipo</span>
          <select
            aria-label="Tipo"
            value={kind}
            onChange={(e) =>
              setKind(e.target.value as "all" | Redemption["kind"])
            }
            style={{ ...inputStyle, width: "auto" }}
          >
            <option value="all">Tipo · todos</option>
            <option value="benefit">Beneficio</option>
            <option value="reward">Recompensa</option>
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="label">Estado</span>
          <select
            aria-label="Estado"
            value={reversed}
            onChange={(e) =>
              setReversed(e.target.value as "all" | "false" | "true")
            }
            style={{ ...inputStyle, width: "auto" }}
          >
            <option value="all">Estado · todos</option>
            <option value="false">Vigentes</option>
            <option value="true">Revertidos</option>
          </select>
        </label>
      </section>

      {redemptions.isLoading ? (
        <div style={{ padding: 22 }}>Cargando consumos…</div>
      ) : redemptions.isError ? (
        <AlertText style={{ padding: 22 }}>
          No se pudo cargar la lista. Intenta de nuevo en unos segundos.
        </AlertText>
      ) : rows.length ? (
        <CursorTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          page={{
            nextCursor: lastPage?.meta.next ?? null,
            totalCount: lastPage?.meta.total ?? rows.length,
          }}
          onLoadMore={() => redemptions.fetchNextPage()}
          isLoadingMore={redemptions.isFetchingNextPage}
          minWidth={1180}
          // Revisión en vivo 2026-09-22, punto #10: "Revertir" era el caso
          // más grave del punto. Dibujado con `.btn-link` —texto plano gris,
          // sin fondo ni borde— no se leía como una acción, y menos como una
          // que devuelve puntos y deshace un consumo. Pasa a ícono + tooltip
          // con el MISMO tono de peligro que "Inactivar" en Inicio: la
          // consecuencia es equivalente, así que la señal visual también.
          rowActions={(r) =>
            !r.reversedAt ? (
              <Can module="members" action="canEdit">
                <RowIconAction
                  label="Revertir"
                  icon="revert"
                  tone="danger"
                  onClick={() => startReverse(r)}
                />
              </Can>
            ) : null
          }
        />
      ) : (
        <p
          role="status"
          style={{
            padding: "30px 20px 34px",
            textAlign: "center",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
            borderTop: `1px solid ${tokens.surface.line}`,
          }}
        >
          No hay consumos que coincidan con los filtros.
        </p>
      )}

      {reversing && reverseStep === "form" ? (
        <ReverseForm
          onSubmit={submitReverseForm}
          onCancel={closeReverse}
          submitting={reverseMutation.isPending}
          notice={reverseNotice}
          modal={{
            label: "Revertir consumo",
            width: 480,
            onClose: closeReverse,
          }}
        />
      ) : null}

      {reversing ? (
        <ImpactConfirmDialog
          open={reverseStep === "confirm-points"}
          eyebrow="Consumos"
          title="Revertir consumo"
          message={confirmPointsMessage(pointsToReturn)}
          confirmLabel="Revertir consumo"
          busy={reverseMutation.isPending}
          onCancel={closeReverse}
          onConfirm={confirmReverse}
        />
      ) : null}

      {reversing ? (
        <ImpactConfirmDialog
          open={reverseStep === "confirm-benefit"}
          eyebrow="Consumos"
          title="Revertir consumo"
          message={CONFIRM_BENEFIT_MESSAGE}
          confirmLabel="Revertir consumo"
          busy={reverseMutation.isPending}
          onCancel={closeReverse}
          onConfirm={confirmReverse}
        />
      ) : null}
    </div>
  );
}
